import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, fmt, Badge, PageHeader } from '../../components/ui';
import { UserPlus } from 'lucide-react';

const INIT = { customer_code: '', customer_name: '', phone: '', brokerage_id: '' };

export default function CustomerMasterPage() {
  const [customers, setCustomers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState(INIT);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/masters/brokerages').then(r => setBrokers(r.data));
    load();
  }, []);

  const load = (q = '') => api.get('/masters/customers', { params: { search: q } }).then(r => setCustomers(r.data));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_code || !form.customer_name) { toast.error('Code and name required'); return; }
    setLoading(true);
    try {
      if (editId) { await api.put(`/masters/customers/${editId}`, { ...form, is_active: true }); toast.success('Updated!'); }
      else { await api.post('/masters/customers', form); toast.success('Customer added!'); }
      setForm(INIT); setShowForm(false); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const startEdit = (c) => {
    setForm({ customer_code: c.customer_code, customer_name: c.customer_name, phone: c.phone || '', brokerage_id: c.brokerage_id || '' });
    setEditId(c.id); setShowForm(true);
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        title="Customer Master"
        subtitle="Manage investor accounts"
        action={<Btn onClick={() => { setShowForm(v => !v); setEditId(null); setForm(INIT); }}><UserPlus size={15} /> Add Customer</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 22 }}>
          <CardHeader title={editId ? 'Edit Customer' : 'New Customer'} subtitle="Brokerage plan is applied to all transactions for this customer" />
          <form onSubmit={submit} style={{ padding: 22 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 14 }}>
              <Input label="Customer Code *" value={form.customer_code} onChange={set('customer_code')} placeholder="e.g. CUST001" disabled={!!editId} />
              <Input label="Customer Name *" value={form.customer_name} onChange={set('customer_name')} placeholder="Full name" />
              <Input label="Phone Number" type="tel" value={form.phone} onChange={set('phone')} placeholder="10-digit mobile" maxLength={15} />
              <Select label="Brokerage Plan" value={form.brokerage_id} onChange={set('brokerage_id')}>
                <option value="">No plan assigned</option>
                {brokers.map(b => <option key={b.id} value={b.id}>{b.plan_name} — {fmt(b.amount)}</option>)}
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" disabled={loading}>{loading ? 'Saving…' : editId ? 'Update' : 'Add Customer'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader
          title="All Customers"
          subtitle={`${customers.length} customers`}
          action={
            <input
              style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', width: 200 }}
              placeholder="Search…" value={search}
              onChange={e => { setSearch(e.target.value); load(e.target.value); }}
            />
          }
        />
        <Table
          headers={['Code', 'Name', 'Phone', 'Brokerage Plan', 'Plan Amount', 'Actions']}
          rows={customers.map(c => [
            <strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{c.customer_code}</strong>,
            c.customer_name,
            c.phone || <span style={{ color: '#94a3b8' }}>—</span>,
            c.plan_name ? <Badge color="blue">{c.plan_name}</Badge> : <span style={{ color: '#94a3b8', fontSize: 12 }}>Not assigned</span>,
            c.brokerage_amount ? <strong style={{ color: '#0f172a' }}>{fmt(c.brokerage_amount)}</strong> : '—',
            <Btn size="sm" variant="ghost" onClick={() => startEdit(c)}>Edit</Btn>
          ])}
          empty="No customers. Add your first customer above."
        />
      </Card>
    </div>
  );
}
